import _ from 'lodash';
import HttpCodes from 'http-status-codes';
import { errorToast } from '@/hybrid/utilities/toast.utilities';
import { isCanceled } from '@/hybrid/utilities/http.utilities';
import { getWorkstep, pushWorkstep } from '@/hybrid/worksteps/worksteps.utilities';
import { WORKSTEP_PUSH_DISABLED } from '@/hybrid/worksteps/worksteps.constant';
import { flux } from '@/core/flux.module';
import { PUSH_IGNORE } from '@/core/flux.service';
import { sqWorkstepsStore } from '@/core/core.stores';

/**
 * Pushes worksheet state on the workstep queue
 *
 * @param workbookId - The workbook id to push.
 * @param worksheetId - The worksheet id to push.
 * @param worksheetState An object containing the current state of the worksheet
 * @returns {Promise} A promise that resolves once the worksheet state has been pushed as a workstep
 */
export function pushWorkstepAction(workbookId: string, worksheetId: string, worksheetState: object) {
  return pushWorkstep(workbookId, worksheetId, worksheetState)
    .then((response) => {
      flux.dispatch('WORKSTEPS_SET', _.pick(response, 'previous', 'current', 'next', 'last'), PUSH_IGNORE);
      return response;
    })
    .catch((error) => {
      if (error !== WORKSTEP_PUSH_DISABLED) {
        // Push failed, so clear store
        clearWorkstepAction(!isCanceled(error), _.get(error, 'status'));
      }

      return Promise.reject(error);
    });
}

/**
 * Clears the worksteps store, with an optional error flag. Displays a notification when first transitioning to an
 * error state and the status code is not 403. We don't display a notification when the response is 403 because
 * the HttpHelpers forbiddenInterceptor automatically routes to the unauthorized route.
 *
 * @param isError - True if worksteps are being cleared due to an error that occurred
 * @param status - an optional HTTP status code for the error
 */
export function clearWorkstepAction(isError = false, status?: number) {
  if (isError && !sqWorkstepsStore.isError && status !== HttpCodes.FORBIDDEN) {
    errorToast({ messageKey: 'WORKSTEPS.ERROR' });
  }

  flux.dispatch(
    'WORKSTEPS_SET',
    {
      previous: undefined,
      current: {},
      next: undefined,
      last: undefined,
      isError,
    },
    PUSH_IGNORE,
  );
}

/**
 * Gets the current workstep
 *
 * @param workbookId - The workbook ID
 * @param worksheetId - The worksheet ID
 * @returns {Object} The current workstep
 */
export function currentWorkstepAction(workbookId: string, worksheetId: string): Promise<object> {
  return getWorkstepAction(workbookId, worksheetId);
}

/**
 * Gets the next workstep and set it as the current one.
 *
 * @param workbookId - The workbook ID
 * @param worksheetId - The worksheet ID
 * @returns {Promise} A promise that resolves with the next workstep
 */
export function nextWorkstepAction(workbookId: string, worksheetId: string) {
  return sqWorkstepsStore.next
    ? getWorkstepAction(workbookId, worksheetId, sqWorkstepsStore.next, true)
    : Promise.reject();
}

/**
 * Gets the last workstep and set it as the current one.
 *
 * @param workbookId - The workbook ID
 * @param worksheetId - The worksheet ID
 * @returns {Promise} A promise that resolves with the last workstep
 */
export function lastWorkstepAction(workbookId: string, worksheetId: string) {
  return sqWorkstepsStore.last
    ? getWorkstepAction(workbookId, worksheetId, sqWorkstepsStore.last, true)
    : Promise.reject();
}

/**
 * Gets the previous workstep and set it as the current one.
 *
 * @param workbookId - The workbook ID
 * @param worksheetId - The worksheet ID
 * @returns {Promise} A promise that resolves with the previous workstep
 */
export function previousWorkstepAction(workbookId: string, worksheetId: string) {
  return sqWorkstepsStore.previous
    ? getWorkstepAction(workbookId, worksheetId, sqWorkstepsStore.previous, true)
    : Promise.reject();
}

/**
 * Gets the specified workstep and sets it in the store.
 *
 * @param workbookId - The workbook ID
 * @param worksheetId - The worksheet ID
 * @param workstepId - The workstep ID. If undefined the current workstep will be found.
 * @param setAsCurrent - True if it should be set as the current workstep on the server. Only
 *   applicable if `workstepId` is set.
 * @return {Promise} A promise that resolves with the workstep information.
 */
export function getWorkstepAction(
  workbookId: string,
  worksheetId: string,
  workstepId?: string,
  setAsCurrent?: boolean,
): Promise<object> {
  return getWorkstep(workbookId, worksheetId, workstepId, setAsCurrent).then((response) => {
    // PUSH_IGNORE is important here because this method is called as part of the resolve when rehydrating a
    // worksheet. Even though the worksheet store does not dehydrate any of its state a push during the rehydrate
    // process can result in extra worksteps on the wrong worksheet. cloneDeep is used to ensure that the
    // "current" value isn't frozen because that state may be modified before being sent to the state
    // synchronizer
    flux.dispatch('WORKSTEPS_SET', _.cloneDeep(_.pick(response, 'previous', 'current', 'next', 'last')), PUSH_IGNORE);
    return response;
  });
}
